import React from "react";
import RevealAnimation from "../RevealAnimation";
import { Link } from "gatsby";
import "./insightsHeroSection.scss";

/**
 * Reusable hero for /insights, /insights/articles, /insights/white-papers
 * Props:
 *  - title: string (e.g. "Insights & Articles")
 *  - titleHighlight: string (the word to highlight, e.g. "Articles")
 *  - subtitle: string
 *  - breadcrumbs: [{ label, to }] - last one is current (no link)
 *  - tabs: [{ label, to, icon, active }]
 *  - stats: [{ value, label }] - optional right-side stats
 */
function InsightsHeroSection({
  title = "Insights",
  titleHighlight = "",
  subtitle = "",
  breadcrumbs = [],
  tabs = [],
  stats = [],
}) {
  const renderTitle = () => {
    if (!titleHighlight) return title;
    const idx = title.indexOf(titleHighlight);
    if (idx === -1) return title;
    const before = title.substring(0, idx);
    const after = title.substring(idx + titleHighlight.length);
    return (
      <>
        {before}
        <span className="title-highlight">{titleHighlight}</span>
        {after}
      </>
    );
  };

  return (
    <section className="insights-hero-section">
      {/* Background layers */}
      <div className="hero-bg-layer">
        <div className="hero-mesh-grid" />
        <div className="hero-glow hero-glow--1" />
        <div className="hero-glow hero-glow--2" />
        <div className="hero-glow hero-glow--3" />
        <div className="hero-particles">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`particle particle--${i + 1}`} />
          ))}
        </div>
        <div className="hero-noise" />
      </div>

      <div className="insights-hero-inner body-container">
        <div className="hero-left">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <RevealAnimation
              component="nav"
              className="hero-breadcrumbs"
              delay={0}
              animation="fade"
            >
              {breadcrumbs.map((crumb, i) => {
                const isLast = i === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={i}>
                    {isLast ? (
                      <span className="crumb crumb--active">{crumb.label}</span>
                    ) : (
                      <>
                        <Link to={crumb.to} className="crumb">
                          {crumb.label}
                        </Link>
                        <span className="crumb-divider">
                          <svg
                            width="7"
                            height="10"
                            viewBox="0 0 7 10"
                            fill="none"
                          >
                            <path
                              d="M1 1L5 5L1 9"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </RevealAnimation>
          )}

          {/* Tagline */}
          <RevealAnimation
            component="div"
            className="hero-tagline"
            delay={0.05}
            animation="fade"
          >
            <span className="tagline-dot" />
            Explore. Learn. Innovate.
          </RevealAnimation>

          {/* Title */}
          <RevealAnimation
            component="h1"
            className="hero-main-title"
            delay={0.1}
            animation="slide-up"
          >
            {renderTitle()}
          </RevealAnimation>

          {/* Subtitle */}
          {subtitle && (
            <RevealAnimation
              component="p"
              className="hero-description"
              delay={0.2}
              animation="slide-up"
            >
              {subtitle}
            </RevealAnimation>
          )}

          {/* Tabs */}
          {tabs.length > 0 && (
            <RevealAnimation
              component="div"
              className="hero-nav-tabs"
              delay={0.3}
              animation="slide-up"
            >
              {tabs.map((tab, i) => (
                <Link
                  key={i}
                  to={tab.to}
                  className={`nav-tab ${tab.active ? "nav-tab--active" : ""}`}
                >
                  {tab.icon && <span className="nav-tab-icon">{tab.icon}</span>}
                  <span className="nav-tab-label">{tab.label}</span>
                  {tab.active && <span className="nav-tab-indicator" />}
                </Link>
              ))}
            </RevealAnimation>
          )}
        </div>

        {/* Right side - stats or visual */}
        {stats.length > 0 && (
          <div className="hero-right">
            <RevealAnimation
              className="hero-stats-grid"
              delay={0.3}
              animation="slide-left"
            >
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="stat-card"
                  style={{ animationDelay: `${0.4 + i * 0.1}s` }}
                >
                  <div className="stat-icon-ring" />
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </RevealAnimation>
          </div>
        )}
      </div>

      {/* Bottom decorative line */}
      <div className="hero-bottom-accent" />
    </section>
  );
}

export default InsightsHeroSection;
